<template>
  <v-container fluid v-resize="onResize" class="px-0 py-0">
    <slot name="bar" :itemListCommon="itemListCommon">
      <v-system-bar height="24">
        <span v-if="!!title"><span class="text-h6">{{ title }}</span></span>
        <v-spacer></v-spacer>
        <v-icon
            v-if="!!itemListCommon && !!itemListCommon.tableLastUpdateFrom"
            class="px-0"
        >mdi-import
        </v-icon>
        <v-chip
            x-small
            v-if="!!itemListCommon && !!itemListCommon.tableLastUpdateFrom && itemListCommon.tableUpdateFromPercent === 100"
            class="px-1"
        >{{ tableLastUpdateFrom | moment("DD.MM.YY HH:mm") }}
        </v-chip>
        <v-col
            cols="1"
            v-if="!!tableUpdateFromPercent && tableUpdateFromPercent < 100"
        >
          <v-progress-linear
              color="green lighten-3"
              height="16"
              top
              :value="tableUpdateFromPercent"
          >
            <template v-slot:default="{ value }">
              <small>{{ value }}%</small>
            </template>
          </v-progress-linear>
        </v-col>
        <v-icon
            v-if="!!itemListCommon.tableLastUpdateTo"
            class="px-0"
        >mdi-export
        </v-icon>
        <v-chip
            x-small
            v-if="!!itemListCommon.tableLastUpdateTo && itemListCommon.tableUpdateToPercent === 100"
            class="px-1"
        >{{ tableLastUpdateTo | moment("DD.MM.YY HH:mm") }}
        </v-chip>
        <v-col
            cols="1"
            v-if="!!itemListCommon.tableUpdateToPercent && itemListCommon.tableUpdateToPercent < 100">
          <v-progress-linear
              color="blue lighten-3"
              height="16"
              top
              :value="tableUpdateToPercent"
          >
            <template v-slot:default="{ value }">
              <small>{{ value }}%</small>
            </template>
          </v-progress-linear>
        </v-col>

        <BaseTextSwitch
            v-if="!!tableProperties"
            v-model="tableProperties.show_deleted"
            x-small
            on-text="show deleted"
            off-text="show undeleted"
            @input="onUpdateFilters()"
            :color="tableProperties.show_deleted ? 'red lighten-1': null"
        ></BaseTextSwitch>

        <BasePageMenu
            :page-properties="tableProperty"
            :fetch-action="fetchAction"
            :selected-item-list.sync="selectedItemList"
        ></BasePageMenu>
      </v-system-bar>
    </slot>
    <v-data-table
        ref="DataTable"
        v-model="selectedItemList"
        dense
        :headers="headers || getLocaleColumns"
        :items="itemList"
        :item-key="itemKey"
        class="elevation-1"
        :options.sync="options"
        :server-items-length="totalRecords"
        @update:options="updateOptions"
        :loading="loading"
        :height="componentHeight - subtractHeight - 8"
        :item-class="defaultRowBackGround"
        v-bind="$attrs"
        :show-select="showSelected || actionSelected"
        :single-select="singleSelect"
        @click:row="handleClick"
        :footer-props="{
          'items-per-page-options': [-1, 20, 50, 100, 250]
        }"
        :expanded.sync="expanded"
        :show-expand="showExpand"
        :single-expand="singleExpand"
        @item-selected="$emit('item-selected', $event)"
        :hide-default-footer="hideDefaultFooter"
    >
      <template v-slot:top>
        <slot name="top" :instance="this">
          <v-toolbar flat dense>
            <slot name="top.bar" :instance="instance" :selectedItems="selectedItemList">
              <v-row class="flex-nowrap">
                <slot name="top.bar.status" :instance="instance" :selectedItems="selectedItemList">
                  {{''}}
                </slot>
                <slot name="top.bar.filter" :instance="instance" :selectedItems="selectedItemList">
                  <BaseFilterGroupTable
                      v-if="!!itemListCommon.table_filters"
                      v-model="itemListCommon.table_filters"
                      @change-filters="onChangeFilters($event)"
                  ></BaseFilterGroupTable>
                </slot>
              </v-row>
              <slot name="top.bar.search" :instance="instance" :selectedItems="selectedItemList">
                <v-text-field
                    dense
                    class="mt-4 mr-2"
                    v-model="searchTerm"
                    :label="$t('search')"
                    clearable
                    @input="onUpdateSearchTerm"
                ></v-text-field>
              </slot>
              <slot name="top.bar.action" :instance="instance" :selectedItems="selectedItemList">
                <v-row class="flex-nowrap">
                  <slot name="top.bar.action.left" :instance="instance" :selectedItems="selectedItemList">
                    {{ '' }}
                  </slot>
                  <slot name="top.bar.action.middle" :instance="instance" :selectedItems="selectedItemList">
                    <BaseBtnActionGroup
                        :bar-action-group="itemListCommon.table_actions"
                        :fetch-action="fetchAction"
                        :selected-items="selectedItemList"
                        :loading="commandLoading"
                    >
                    </BaseBtnActionGroup>
                  </slot>
                  <slot name="top.bar.action.right" :instance="instance" :selectedItems="selectedItemList">
                    <v-btn
                        v-if="!disableNew"
                        small
                        @click.stop="openDialog(null)"
                    >
                      {{ $t('new_item') }}
                    </v-btn>
                    <slot name="top.bar.action.right.add" :instance="instance" :selectedItems="selectedItemList"></slot>
                    <v-btn
                        v-if="actionSelected"
                        small
                        :disabled="selectedItemList.length === 0"
                        @click="$emit('choice', selectedItemList)"
                    >Select
                    </v-btn>
                  </slot>
                </v-row>
              </slot>
            </slot>
          </v-toolbar>
        </slot>
      </template>
      <template v-for="header in headers || getLocaleColumns" v-slot:[`item.${header.value}`]="{item}">
        <slot :name="[`item.${header.value}`]" :item="item" :instance="instance">
          <span v-if="header.type === 'bool'">
            <v-simple-checkbox small :value="item[`${header.value}`]"></v-simple-checkbox>
          </span>
          <span v-else-if="header.type === 'date'">
            {{ item[`${header.value}`] | moment("DD.MM.YY") }}
          </span>
          <span v-else-if="header.type === 'datetime'">
            {{ item[`${header.value}`] | moment("DD.MM.YY HH:mm") }}
          </span>
          <span v-else-if="header.type === 'status'">
            <BaseStatusIcon :value="item[`${header.value}`]"></BaseStatusIcon>
          </span>
          <span v-else>
            {{ item[`${header.value}`] }}
          </span>
        </slot>
      </template>
      <template v-slot:item.drag="{ item }">
        <div class="handle grab">
          <v-icon small class="d-flex justify-center">mdi-drag-variant</v-icon>
        </div>
      </template>
      <template v-slot:item.deleted="{ item }">
        <slot name="item.deleted" :item="item" :instance="instance">
          <v-icon v-if="!!item.deleted" small color="red lighten-2" class="d-flex justify-center">mdi-trash-can</v-icon>
        </slot>
      </template>
      <template v-slot:item.edit="{ item }">
        <slot name="item.edit" :item="item" :instance="instance">
          <v-icon small @click="openDialog(item)" color="edit_icon" class="d-flex justify-center">mdi-pencil-outline
          </v-icon>
        </slot>
      </template>
      <template v-slot:item.action="{ item }">
        <slot name="item.action" :item="item" :instance="instance">
          <v-icon small @click="$emit('choiceAction', item)">mdi-radiobox-blank</v-icon>
        </slot>
      </template>
      <template v-slot:footer.prepend>
        <span v-if="!!instance && instance.selectedItemList.length > 0">
          {{ instance.selectedItemList.length }} of {{ instance.totalRecords }} selected
        </span>
      </template>
    </v-data-table>
    <v-dialog
        v-model="dialogItem"
        persistent
        v-if="!!newItemComponent && !!newItemPass"
    >
      <component
          :item-uid="itemUid"
          :is="currentNewItem"
          :modalDialog="true"
          :route-full-screen="{name: newItemComponent, params: {itemUid: itemUid}}"
          :parent-uid="parentUid"
          :metadata="metadata"
          @change-search-term="onChangeSearchTerm"
          @save="onSaveComponent"
      ></component>
    </v-dialog>
    <ConfirmDialog :confirm_request="confirm_request" :fetch-request="fetchRequest"></ConfirmDialog>
    <router-view></router-view>
  </v-container>
</template>

<script>
import {debounce} from "debounce"
import {v4 as uuidv4} from 'uuid'
import {mapGetters, mapActions} from 'vuex';
import {mapFields} from 'vuex-map-fields';
import ConfirmDialog from "@/components/Base/ConfirmDialog";
import colorClass from "@/commonFunction/colorClassDict"
import Sortable from "sortablejs";
import commonFunction from "@/commonFunction/commonFunction"
import {defineAsyncComponent, ref} from 'vue';
import BasePageMenu from "@/components/Base/BasePageMenu";
import BaseTextSwitch from "@/components/Base/BaseTextSwitch";
import BaseStatusIcon from "@/components/Base/BaseStatusIcon";
import BaseFilterGroupTable from "@/components/Base/BaseFilterGroupTable";
import BaseBtnActionGroup from "@/components/Base/BaseBtnActionGroup";

export default {
  name: 'BaseTableFiltered',
  props: {
    title: {type: [String], default: null},
    subtractHeight: {type: [Number], default: 90},
    height: {type: [Number], default: null},
    dtoModel: {type: [String], default: null},
    itemKey: {type: [String], default: 'id'},
    dtoSaveListModel: {type: [String], default: null},
    storeName: {type: [String], default: null},
    newItemComponent: {type: [String], default: null},
    newItemPass: {type: [String], default: null},
    disableNew: {type: [Boolean], default: false},
    showSelected: {type: [Boolean], default: false},
    showExpand: {type: [Boolean], default: false},
    singleExpand: {type: [Boolean], default: false},
    singleSelect: {type: [Boolean], default: true},
    langDictionary: {
      type: [Function], default: function (text) {
        return text
      }
    },
    modalDialog: {type: [Boolean], default: false},
    actionSelected: {default: false},
    filtersInit: {type: Array, default: () => []},
    selectedUidInit: {type: Array, default: () => []},
    searchInit: {type: String, default: null},
    parentUid: {default: null},
    metadata: {default: null},
    headers: {default: null},
    initSearchTerm: {default: null},
    hideDefaultFooter: {default: false},
    rowStatuses: {default: () => []},
  },

  components: {
    ConfirmDialog,
    'add-component': () => import(self.newItemPass),
    BasePageMenu,
    BaseTextSwitch,
    BaseStatusIcon,
    BaseFilterGroupTable,
    BaseBtnActionGroup,
  },

  watch: {
    filtersInit: {
      handler(newValue, oldValue) {
        this.filtersListConst = JSON.parse(JSON.stringify(newValue))
        this.onUpdateFilters()
      },
      deep: true
    },
    initSearchTerm: {
      handler(newValue, oldValue) {
        // if (!!newValue) {
        // console.log('initSearchTerm', newValue)
        this.searchTerm = newValue
        this.onUpdateFilters()
        this.$emit('changeSearchTerm', newValue)
        // }
      },
      deep: false
    },
    searchInit: {
      handler(newValue, oldValue) {
        this.searchTerm = newValue
        this.onUpdateFilters()
      },
      deep: false
    },
    searchTerm: {
      handler(newValue, oldValue) {
        this.$emit('change_search', newValue)
      },
      deep: false
    },
    itemList(newValue, oldValue) {
      // console.log('watch itemList', newValue, oldValue)
      if (newValue.length != 0 && oldValue.length === 0)
        this.selected_number = 0
      if (newValue.length != 0) {
        // console.log('itemList', newValue, this.selectedUidInit)
        this.selectedItemList = newValue.filter(row => this.selectedUidInit.includes(row.uid))
      }
    }
  },

  computed: {
    ...commonFunction.mapDynamicGetters('storeName', ['getColumns']),
    ...commonFunction.mapDynamicFields('storeName', ['message_error', 'options', 'totalRecords',
      'options.page', 'itemList', 'loading', 'commandLoading', 'selectedItemList', 'searchTermFile', 'searchTerm',
      'errorMessage', 'confirm_request', 'editedItem', 'filters', 'dialogItem', 'itemListCommon',
      'tableUpdateFromPercent', 'tableUpdateToPercent', 'tableLastUpdateFrom', 'tableLastUpdateTo', 'filtersListConst',
      'tableProperty', 'group_filters'
    ]),

    itemUid() {
      return this.childUid
    },

    currentNewItem: function () {
      if (!!this.newItemPass) {
        // console.log('currentNewItem', this.newItemPass)
        return () => import("@/components" + this.newItemPass);
        // return() => import(this.newItemPass);
      }
    },

    getLocaleColumns() {
      let columns = []
      for (let column of this.getColumns) {
        let text = this.langDictionary('columns.' + column.value)
        if (!!text)
          column.text = text
        columns.push(column)
      }
      return columns
    },
    componentHeight() {
      if (!!this.height) {
        this.$emit('resize', this.height)
        return this.height
      }
      const heightOffset = 30
      this.$emit('resize', this.windowSize.y * 0.9 - heightOffset)
      return this.windowSize.y * 0.9 - heightOffset
    },
  },

  methods: {
    ...commonFunction.mapDynamicActions('storeName', ['openWebSocket', 'fetchItemList', 'fetchItem',
      'fetchSaveItemList', 'fetchAction',
      'fetchSaveItem', 'fetchActionSelectedItem', 'fetchActionItem', 'fetchRequest', 'fetchSaveEditedItem',
      'fetchSaveItem', 'fetchRemoveItem']),

    onResize() {
      this.windowSize = {x: window.innerWidth, y: window.innerHeight};
    },

    defaultRowBackGround(item) {
      // console.log('this.rowStatuses', this.rowStatuses)
      if (!!item && !!this.rowStatuses && item[this.rowStatuses] !== undefined) {
        if (item[this.rowStatuses].error_status === 'ERROR')
          return 'red lighten-4'
        if (item[this.rowStatuses].error_status === 'WARNING')
          return 'orange lighten-4'
        if (item[this.rowStatuses].error_status === 'SUCCESS')
          return 'green lighten-4'
      }
    },

    onSaveComponent(item) {
      commonFunction.updateDeepDictionary(this.itemList, item)
      this.$emit('save', item)
    },

    onChangeFilters(group_filters) {
      console.log('onChangeFilters', group_filters)
      this.group_filters = group_filters
      this.onUpdateFilters()
    },

    updateOptions() {
      // console.log('updateOptions')
      if (!this.filters)
        this.filters = []
      if (this.tableProperties.show_deleted === false) {
        this.filters = this.filters.filter(x => x.param_compare !== 'deleted')
        this.filters.push({param_compare: 'deleted', value: false})
      } else {
        this.filters = this.filters.filter(x => x.param_compare !== 'deleted')
        this.filters.push({param_compare: 'deleted', value: true})
      }
      // console.log('updateOptions', this.filters)
      this.fetchItemList(this.dtoModel)
    },

    onUpdateFilters() {
      // console.log('onUpdateOptions')
      this.options.page = 1
      this.updateOptions()
    },

    onChangeSearchTerm(searchTerm) {
      // console.log('onChangeSearchTerm', searchTerm)
      this.searchTerm = searchTerm
      this.onUpdateFilters()
    },

    openDialog(item) {
      if (!!item)
        this.childUid = item.uid
      else
        this.childUid = uuidv4()
      this.dialogItem = true
    },

    saveOrder({newIndex, oldIndex}) {
      const row = this.itemList.splice(oldIndex, 1)[0]
      this.itemList.splice(newIndex, 0, row)
      let itemList = []
      this.itemList.forEach(function (item, index) {
        item.order = index;
        itemList.push(item)
      })
      // console.log('saveOrder', this.itemList)
      this.fetchSaveItemList({dto_save_list_model: this.dtoSaveListModel, itemList: this.itemList})
    },

    onUpdateSearchTerm: debounce(function () {
      // console.log('onUpdateSearchTerm', this.filters, this.searchTerm)
      this.$emit('changeSearchTerm', this.searchTerm)
      this.options.page = 1
      this.fetchItemList(this.dtoModel)
    }, 800),

    isUpdateFromShow() {
      return true
    },

    isUpdateToShow() {
      return true
    },

    handleClick(item) {
      // console.log('row click', item, this.selectedUid)
      if (this.selectedUid !== item.uid) {
        this.selectedUid = item.uid
        this.$emit('change_current_uid', this.selectedUid)
      }
      this.$emit('click:row', item)
    },
    onKey(event) {
      // console.log('key', event)
      if (event.target.nodeName !== 'TEXTAREA' &&
          event.key === "ArrowUp" && this.selected_number > 0) {
        this.selected_number -= 1
      }
      if (event.target.nodeName !== 'TEXTAREA' &&
          event.key === "ArrowDown" && this.selected_number < this.totalRecords - 1) {
        this.selected_number += 1
      }
    },
  }
  ,

  async mounted() {
    // console.log('mounted BaseTable', this.filtersInit)
    this.filtersListConst = JSON.parse(JSON.stringify(this.filtersInit))
    // this.filters = [{param_compare: 'deleted', value: false}]
    // this.searchTerm = this.searchInit
    let table = document.querySelector(".v-data-table__wrapper tbody")
    Sortable.create(table, {handle: ".handle", sort: true, onUpdate: this.saveOrder})
    this.onResize()
  }
  ,

  created() {
    window.addEventListener('keyup', this.onKey)
  }
  ,

  destroyed() {
    window.removeEventListener("keyup", this.onKey);
  }
  ,

  beforeCreate() {
  }
  ,

  beforeDestroy() {
  }
  ,

  data() {
    return {
      self: this,
      expanded: [],
      windowSize: {x: 0, y: 0,},
      tableProperties: {
        show_deleted: false,
      },
      instance: this,
      childUid: null,
      selectedUid: null,
    }
  }
}
</script>

<style>
.row_even {
  background-color: white !important;
}

.row_selected {
  background-color: #CFD8DC !important;
}
</style>

<i18n>
{
  "en": {
    "search": "Search",
    "Save": "Save",
    "Cancel": "Cancel",
    "new_item": "Add",
    "show_deleted": "Show deleted"
  },
  "de": {
    "search": "Suche",
    "Save": "Speichern",
    "Cancel": "Stornieren",
    "new_item": "Hinzufügen",
    "show_deleted": "Sendung gelöscht"
  }
}
</i18n>
